import { bandFor } from '../entity/bands';
import {
  capturedAt,
  flat,
  kindOf,
  listOf,
  memberRefs,
  parseEntity,
  picksIn,
  sectionNamed,
  SOURCE_ROLE,
  transcriptTopics,
} from '../entity/entity';
import type { Topic } from '../entity/entity';
import { readThought } from '../thoughts/thought';
import { editableText, ownText } from '../writing/textNote';
import { refKey } from '../plane/planeKey';
import { collapsedLines } from '../plane/cardSize';
import type { PlanePlacement, PlaneRef, StickyTint } from '../plane/planeKey';
import type { CatalogNote } from '../entity/catalog';
import { nameOf, parseWorkspace } from '../workspaces/workspace';
import type { Workspace } from '../workspaces/workspace';
import { hiddenStateOf } from '../entity/visibility';
import { bestMatches } from '../palette/fuzzy';
import { itemFor } from '../thoughts/corpus';
import type { CorpusItem } from '../thoughts/corpus';
import type { Arc, Status } from '../plan/arc';
import { openInside } from '../plan/arc';
import { recencyOf } from '../plan/chronology';
import { readList, withList } from '../plan/edits';
import { isArcIn, kindByFolder } from '../vault/kinds';
import { readLinks, readPlane, withLinks, withPlane } from '../plane/planeKey';

/**
 * A nook, read off its file: what it holds, and what each held thing is on the
 * plane.
 *
 * Membership and placement are separate on purpose. What the nook HOLDS is its
 * `arcs:` and its `## Members` lines; what is ON THE PLANE is only what has a
 * `plane:` entry. Nothing is placed for you — a nook with no entries is an
 * empty plane, never a seeded layout of its members.
 */

export type ItemKind = 'arc' | 'recording' | 'note' | 'nook' | 'other';

/** The arcs a nook holds, as vault paths, in its order. */
export function heldArcs(frontMatter: Record<string, string | string[]>): string[] {
  return listOf(frontMatter['arcs']).flatMap((entry) => {
    const path = arcEntryPath(entry);
    return path ? [path] : [];
  });
}

/** The vault path an `arcs:` entry names, whichever way it was written. */
export function arcEntryPath(entry: string): string | null {
  const p = entry.split(' ^')[0]!.trim().replace(/^\[\[|\]\]$/g, '');
  if (!p.endsWith('.md')) return null;
  return p.includes('/') ? p : `arcs/${p}`;
}

/** `- [label](<path>)` with no ref comment — the line `create_note` writes,
 *  which the importer matches by path and completes on its next export. */
const BARE_MEMBER = /^- \[(.*?)\]\(<?([^)>]*?)>?\)[ \t]*$/;

/**
 * What the nook holds by reference: every source member line, then any bare
 * member line not yet completed by the app. One entry per path and anchor — a
 * topic is its own placeable thing, and the whole recording is another.
 */
export function heldRefs(raw: string): PlaneRef[] {
  const out: PlaneRef[] = [];
  const seen = new Set<string>();
  const add = (ref: string, anchor: string | null) => {
    const key = anchor ? `${ref}#${anchor}` : ref;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ ref, anchors: anchor ? [anchor] : [] });
  };

  for (const m of memberRefs(raw)) if (m.role === SOURCE_ROLE) add(m.path, m.anchor);

  let inMembers = false;
  for (const line of raw.split('\n')) {
    if (/^#{1,6}\s/.test(line)) {
      inMembers = /^##\s+members\s*$/i.test(line);
      continue;
    }
    if (!inMembers) continue;
    const bare = line.match(BARE_MEMBER);
    if (!bare) continue;
    const dest = bare[2]!;
    const at = dest.indexOf('#^');
    add(at >= 0 ? dest.slice(0, at) : dest, at >= 0 ? dest.slice(at + 2) : null);
  }
  return out;
}

/** What a file is on this page. The file says so itself — a recording and a
 *  note both declare `kind:`, and a note the web wrote has none, which reads
 *  as a note. */
export function itemKind(path: string, raw: string | null): ItemKind {
  if (kindByFolder(path) === 'arc') return 'arc';
  if (raw === null) return 'other';
  const entity = parseEntity(path, raw);
  if (kindOf(entity) === 'arc') return 'arc';
  if (kindOf(entity) === 'workspace') return 'nook';
  const band = bandFor(kindOf(entity), path);
  return band === 'recordings' ? 'recording' : band === 'notes' ? 'note' : 'other';
}

/**
 * The words a recording is recognised by: a held topic's own first paragraph,
 * or the recording's first face pick, with the first spoken line standing in
 * where nothing was picked.
 */
export function recordingFace(path: string, raw: string, anchor: string | undefined): string {
  if (anchor) {
    const topic = transcriptTopics(parseEntity(path, raw)).find((t) => t.anchor === anchor);
    if (topic) return topic.paragraphs[0] ?? topic.title;
  }
  const thought = readThought(path, raw);
  return thought.face[0] ?? thought.standIn;
}

/** `wed 23 sep · 20:23` — the head's one way of saying when. */
export function capturedLabel(path: string, raw: string): string | null {
  const at = capturedAt(parseEntity(path, raw));
  return at ? `${at.day} · ${at.time}` : null;
}

/** A note's prose, as the writing panel would show it. */
export function noteText(path: string, raw: string): string {
  return editableText(raw) ?? parseEntity(path, raw).body;
}

/** A collapsed card's estimated lines ([collapsedLines]), as the app sizes the item it is
 *  (`CardLineInputs`): a topic — an item inside another's file — as one, whatever the file is; an
 *  arc by its name; a note by its own text where [raw] holds it. */
export function collapsedLinesOf(ref: PlaneRef, view: CardView, raw: string | null | undefined): number {
  if (ref.anchors.length > 0) return collapsedLines('topic');
  if (view.kind === 'arc') return collapsedLines('arc', view.title);
  return collapsedLines(view.kind, view.kind === 'note' && raw != null ? ownText(raw) : null);
}

/** What a sticky shows: a leading heading as its title, and the text under it.
 *  Blank lines around the text go; the spaces a line starts with stay.
 *  A note that opens on prose has no title; all of it is the text. */
export function stickyFace(text: string): { title: string | null; text: string } {
  const trimmed = text.replace(/^(?:[ \t]*\n)+/, '');
  const heading = trimmed.match(/^#{1,6}\s+(.*)(?:\n|$)/);
  if (!heading) return { title: null, text: trimmed.replace(/\s+$/, '') };
  return {
    title: heading[1]!.trim() || null,
    text: trimmed.slice(heading[0].length).replace(/^(?:[ \t]*\n)+/, '').replace(/\s+$/, ''),
  };
}

/** A note's name: its first line, which is what anyone would call it. */
export function noteTitle(path: string, raw: string): string {
  const line = noteText(path, raw)
    .split('\n')
    .map((l) => l.trim())
    .find((l) => l !== '');
  return line ? line.replace(/^#{1,6}\s+/, '') : path.replace(/^.*\//, '').replace(/\.md$/, '');
}

export type CardView =
  | { kind: 'arc'; title: string; status: Status; inside: number }
  | { kind: 'recording'; date: string | null; face: string }
  | { kind: 'note'; title: string | null; text: string; tint: StickyTint }
  | { kind: 'nook'; title: string; plane: PlanePlacement[]; nook: Workspace }
  | { kind: 'other'; title: string };

/** The name a card shows for itself: an arc's or file's title, a recording's
 *  face line, a note's title, or its opening words where it has none. A nook
 *  nobody named has none. */
export function cardName(view: CardView | undefined): string | undefined {
  if (!view) return undefined;
  if (view.kind === 'nook') return view.title || undefined;
  if (view.kind === 'arc' || view.kind === 'other') return view.title;
  if (view.kind === 'recording') return view.face || undefined;
  return view.title || view.text || undefined;
}

const NO_CATALOG: ReadonlyMap<string, CatalogNote> = new Map();

/** Files read for the page, by path; null for one the vault does not hold. A
 *  path not in the map has not been read yet. */
export type Files = ReadonlyMap<string, string | null>;

/**
 * How one thing is drawn on the plane, or null while what it needs has not
 * been read — a card appears when it can say something, never as an empty
 * box first.
 */
export function cardView(
  ref: PlaneRef,
  arcs: readonly Arc[] | null,
  files: Files,
  tint?: StickyTint,
  catalog: ReadonlyMap<string, CatalogNote> = NO_CATALOG,
): CardView | null {
  if (isArcIn(ref.ref, arcs)) return arcs ? arcCard(ref.ref, arcs) : null;
  if (!files.has(ref.ref)) return null;
  const text = files.get(ref.ref) ?? null;
  if (text === null) return { kind: 'other', title: bare(ref.ref) };
  const kind = itemKind(ref.ref, text);
  // Read before the plan arrived: drawn once the plan can say its state.
  if (kind === 'arc') return arcs ? arcCard(ref.ref, arcs) : null;
  if (kind === 'recording') {
    return {
      kind,
      date: capturedLabel(ref.ref, text),
      face: recordingFace(ref.ref, text, ref.anchors[0]),
    };
  }
  if (kind === 'note') {
    return { kind, ...stickyFace(noteText(ref.ref, text)), tint: tint ?? 'warm' };
  }
  if (kind === 'nook') {
    const held = parseWorkspace(ref.ref, text);
    if (held) return { kind, title: nameOf(held, catalog).name, plane: held.plane, nook: held };
  }
  return { kind: 'other', title: noteTitle(ref.ref, text) };
}

function arcCard(path: string, arcs: readonly Arc[]): CardView {
  const arc = arcs.find((a) => a.path === path);
  return {
    kind: 'arc',
    title: arc?.title ?? bare(path),
    status: arc?.status ?? 'next',
    inside: arc ? openInside(arc, arcs) : 0,
  };
}

/**
 * A nook that holds nothing yet — no arcs, no members, nothing placed. Only
 * then does it open on the ways to start; a nook holding things it has not
 * placed opens on its empty plane with the dock, where they are one move away.
 */
export function isJustMade(
  placements: readonly PlanePlacement[],
  arcPaths: readonly string[],
  members: readonly PlaneRef[],
): boolean {
  return placements.length === 0 && arcPaths.length === 0 && members.length === 0;
}

/** Exactly what has a `plane:` entry, and nothing else. */
export function placedCards(
  placements: readonly PlanePlacement[],
  arcs: readonly Arc[] | null,
  files: Files,
  catalog: ReadonlyMap<string, CatalogNote> = NO_CATALOG,
): { key: string; placement: PlanePlacement; view: CardView }[] {
  return placements.flatMap((p) => {
    const view = cardView(p, arcs, files, p.tint, catalog);
    return view ? [{ key: refKey(p), placement: p, view }] : [];
  });
}

export type PanelKind = 'arcs' | 'recordings' | 'notes' | 'nooks';

export interface Row {
  ref: PlaneRef;
  title: string;
  status?: Status;
  /** A storyline's open arcs. */
  inside?: number;
  date?: string | null;
  placed?: boolean;
  /** A nooks panel row: the nook it opens. */
  nook?: Workspace;
}

/** One panel's rows, from the nook's membership, in the order it holds them. */
export function panelRows(
  panel: PanelKind,
  arcPaths: readonly string[],
  members: readonly PlaneRef[],
  placements: readonly PlanePlacement[],
  arcs: readonly Arc[] | null,
  files: Files,
): Row[] {
  if (panel === 'arcs') {
    return arcPaths.map((p) => {
      const arc = arcs?.find((a) => a.path === p);
      return {
        ref: { ref: p, anchors: [] },
        title: arc?.title ?? bare(p),
        status: arc?.status ?? 'next',
        inside: arc && arcs ? openInside(arc, arcs) : 0,
      };
    });
  }
  if (panel === 'nooks') return [];
  const placed = new Set(placements.map(refKey));
  const wanted: ItemKind = panel === 'recordings' ? 'recording' : 'note';
  const rows = members.flatMap((m): Row[] => {
    const text = files.get(m.ref) ?? null;
    if (text === null || itemKind(m.ref, text) !== wanted) return [];
    return wanted === 'recording'
      ? [{ ref: m, title: recordingFace(m.ref, text, m.anchors[0]), date: capturedLabel(m.ref, text) }]
      : [{ ref: m, title: noteTitle(m.ref, text), placed: placed.has(refKey(m)) }];
  });
  if (wanted !== 'recording') return rows;
  // A topic sorts with its recording; undated ones go last, in held order.
  const at = (r: Row) => itemFor(r.ref.ref).at?.getTime() ?? -Infinity;
  return rows
    .map((row, i) => ({ row, i, at: at(row) }))
    .sort((a, b) => (a.at === b.at ? a.i - b.i : b.at - a.at))
    .map((e) => e.row);
}

/** How many rows of the whole vault a panel lists at once: the newest, or the
 *  best matches. What the nook holds is never counted against it. */
export const WHOLE_VAULT_ROWS = 40;

/** A nook is only ever placed, and never on its own canvas. */
export function canPlace(nookPath: string, ref: PlaneRef): boolean {
  return ref.ref !== nookPath;
}

/**
 * The panels' rows when they list the whole vault, not only what the nook
 * holds: every live arc, recording, note and nook, newest first, with what the
 * nook holds never counted against [limit] — narrowed by [query].
 * Archived and otherwise hidden items stay out; a done arc is left to the Done
 * pill. The nooks panel is always the whole vault, minus this nook itself.
 */
export function wholeVaultRows({
  panel,
  nookPath,
  arcPaths,
  members,
  arcs,
  corpus,
  nooks,
  catalog,
  query,
  nowMs,
  limit = WHOLE_VAULT_ROWS,
}: {
  panel: PanelKind;
  nookPath: string;
  arcPaths: readonly string[];
  members: readonly PlaneRef[];
  arcs: readonly Arc[] | null;
  corpus: readonly CorpusItem[];
  nooks: readonly Workspace[];
  catalog: ReadonlyMap<string, CatalogNote>;
  query: string;
  nowMs: number;
  limit?: number;
}): Row[] {
  const text = query.trim();
  const narrow = (rows: Row[], held: ReadonlySet<string>, at: (row: Row) => number): Row[] => {
    if (text) return bestMatches(rows, text, (r) => r.title, limit);
    const newest = rows
      .map((row, i) => ({ row, i, at: at(row) }))
      .sort((a, b) => b.at - a.at || a.i - b.i)
      .map((e) => e.row);
    let unheld = 0;
    return newest.filter((r) => held.has(refKey(r.ref)) || unheld++ < limit);
  };

  if (panel === 'nooks') {
    const rows = nooks
      .filter((w) => !w.archived && !w.scratch && w.path !== nookPath)
      .map((w): Row => ({ ref: { ref: w.path, anchors: [] }, title: nameOf(w, catalog).name, nook: w }));
    return text ? bestMatches(rows, text, (r) => r.title, limit) : narrow(rows, new Set(rows.map((r) => refKey(r.ref))), (r) => r.nook?.updatedMs ?? 0);
  }

  const live = (path: string, allowDone = false) => {
    const hidden = hiddenStateOf(catalog.get(path), nowMs);
    return hidden === null || (allowDone && hidden === 'done');
  };

  if (panel === 'arcs') {
    const byPath = new Map((arcs ?? []).map((a) => [a.path, a]));
    const held = new Set(arcPaths.filter((p) => byPath.has(p)));
    const rows = (arcs ?? [])
      .filter((a) => !a.archived && live(a.path, true))
      .map((arc): Row => ({
        ref: { ref: arc.path, anchors: [] },
        title: arc.title,
        status: arc.status,
        inside: arcs ? openInside(arc, arcs) : 0,
      }));
    return narrow(rows, held, (r) => {
      const arc = byPath.get(r.ref.ref);
      return arc ? recencyOf(arc) : 0;
    });
  }

  const wanted = panel === 'recordings' ? 'recording' : 'note';
  const heldKeys = new Set<string>();
  const rows: Row[] = [];
  const seen = new Set<string>();
  const capturedAt = new Map<string, number>();
  const add = (ref: PlaneRef, item: CorpusItem) => {
    const key = refKey(ref);
    if (seen.has(key)) return;
    seen.add(key);
    capturedAt.set(key, item.at?.getTime() ?? 0);
    rows.push({ ref, title: catalog.get(ref.ref)?.name ?? item.name.replace(/\.md$/, '') });
  };
  for (const m of members) {
    const item = itemFor(m.ref);
    if (item.kind !== wanted || !live(m.ref)) continue;
    add(m, item);
    heldKeys.add(refKey(m));
  }
  for (const item of corpus) {
    if (item.kind === wanted && live(item.path)) add({ ref: item.path, anchors: [] }, item);
  }
  return narrow(rows, heldKeys, (r) => capturedAt.get(refKey(r.ref)) ?? 0);
}

function bare(ref: string): string {
  return ref.replace(/^.*\//, '').replace(/\.md$/, '');
}

/** The id of the note Write opens: `write:` on the nook's file, or null when
 *  the nook has none yet and the first Write will make one. By id, because a
 *  note's path moves when it is renamed. */
export function defaultNote(frontMatter: Record<string, string | string[]>): string | null {
  return flat(frontMatter['write']).trim() || null;
}

/**
 * Where the note with [id] lives now, among what the nook holds: its member
 * line carries the id once the app has adopted it, and before that the note's
 * own front matter declares it. Null for a note the nook no longer holds.
 */
export function heldNotePath(
  id: string,
  nookRaw: string,
  files: ReadonlyMap<string, string | null>,
): string | null {
  const member = memberRefs(nookRaw).find((m) => m.id === id && m.anchor === null);
  if (member) return member.path;
  for (const [path, text] of files) if (text !== null && declaredId(path, text) === id) return path;
  return null;
}

/** The id of the held note at [path], by the same two readings as
 *  [heldNotePath]. Null for a note nothing has given an id yet. */
export function heldNoteId(
  path: string,
  nookRaw: string,
  files: ReadonlyMap<string, string | null>,
): string | null {
  const member = memberRefs(nookRaw).find((m) => m.path === path && m.anchor === null);
  if (member) return member.id;
  const text = files.get(path);
  return text ? declaredId(path, text) : null;
}

function declaredId(path: string, raw: string): string | null {
  return flat(parseEntity(path, raw).frontMatter['id']).trim() || null;
}

/**
 * The note with [noteId] made the nook's default note. A targeted edit of one
 * front-matter line, so the body, the member lines and the machine tail survive
 * byte for byte; a file with no front matter is left alone rather than given one.
 */
export function withDefaultNote(raw: string, noteId: string): string {
  const front = raw.match(/^---\n([\s\S]*?)\n---\n/);
  if (!front) return raw;
  const line = `write: ${noteId}`;
  const body = front[1]!;
  const next = /^write:.*$/m.test(body) ? body.replace(/^write:.*$/m, line) : `${body}\n${line}`;
  return `---\n${next}\n---\n${raw.slice(front[0].length)}`;
}

/**
 * A new note held by the nook: a bare member line at the end of `## Members`,
 * the line `create_note` writes and the importer completes by path. A nook
 * with no members yet gets the section, ahead of the machine tail.
 */
export function withMemberLine(raw: string, notePath: string, label: string): string {
  const line = `- [${label.replace(/[[\]]/g, '')}](<${notePath}>)`;
  const lines = raw.split('\n');
  const at = lines.findIndex((l) => /^##\s+members\s*$/i.test(l));
  if (at >= 0) {
    let end = at + 1;
    while (lines[end]?.trim() === '' && lines[end + 1]?.startsWith('- [')) end++;
    while (lines[end]?.startsWith('- [')) end++;
    lines.splice(end, 0, line);
    return lines.join('\n');
  }
  const section = `## Members\n${line}\n\n`;
  const tail = raw.search(/<!--pondering:(?:unit |structure base64)/);
  if (tail >= 0) return raw.slice(0, tail) + section + raw.slice(tail);
  return `${raw.replace(/\n*$/, '\n\n')}${section.trimEnd()}\n`;
}

/**
 * The nook holding a note written on its canvas: the note's member line, and its card where it
 * was started, both added to the file as it is — so one write carries the two and no device sees
 * the note held but not placed.
 */
export function withPlacedNote(raw: string, nookPath: string, notePath: string, at: { x: number; y: number }): string {
  const held = withMemberLine(raw, notePath, bare(notePath));
  const plane = readPlane(parseEntity(nookPath, held));
  if (plane.some((p) => p.ref === notePath && p.anchors.length === 0)) return held;
  return withPlane(held, [...plane, { ref: notePath, anchors: [], x: Math.round(at.x), y: Math.round(at.y) }]);
}

const MEMBER_LINE = /^- \[.*?\]\(<?([^)>]*?)>?\)(?:[ \t]*<!--ref:\S+?((?: [^>]*)?)-->)?[ \t]*$/;

/**
 * [raw] with [ref] no longer held: its `arcs:` entry, or its member line with
 * any ref comment, and — in the same text — its placement on the plane and the
 * connections that end on it, which the app never prunes itself. Every other
 * byte stays, the machine tail included. The nook's talk line is never taken.
 */
export function withoutHeld(raw: string, path: string, ref: PlaneRef): string {
  let text = raw;
  const entries = readList(text, 'arcs');
  const keptArcs = entries.filter((e) => arcEntryPath(e) !== ref.ref);
  if (keptArcs.length !== entries.length) {
    text = withList(text, 'arcs', keptArcs, { stateEmpty: true });
  } else {
    const anchor = ref.anchors[0] ?? null;
    const kept = text.split('\n').filter((line) => {
      const m = line.match(MEMBER_LINE);
      if (!m) return true;
      const role = m[2]?.match(/(?:^| )role:(\S+)/)?.[1] ?? null;
      if (role !== null && role !== SOURCE_ROLE) return true;
      const dest = m[1]!;
      const at = dest.indexOf('#^');
      const linked = at >= 0 ? dest.slice(0, at) : dest;
      const linkedAnchor = at >= 0 ? dest.slice(at + 2) : null;
      return !(linked === ref.ref && linkedAnchor === anchor);
    });
    text = kept.join('\n');
  }

  const key = refKey(ref);
  const nook = parseEntity(path, text);
  const placed = readPlane(nook);
  if (placed.some((p) => refKey(p) === key)) {
    text = withPlane(text, placed.filter((p) => refKey(p) !== key));
  }
  const links = readLinks(parseEntity(path, text));
  if (links.some((l) => refKey(l.from) === key || refKey(l.to) === key)) {
    text = withLinks(text, links.filter((l) => refKey(l.from) !== key && refKey(l.to) !== key));
  }
  return text;
}

/** The arcs that cite this file among their sources — what a recording is
 *  "in". */
export function arcsCiting(path: string, arcs: readonly Arc[] | null): Arc[] {
  if (!arcs) return [];
  return arcs.filter((arc) =>
    listOf(arc.frontMatter['sources']).some((s) => s.split(' ^')[0]!.trim() === path),
  );
}

/**
 * What a recording says when opened: a held topic's own paragraphs, or the
 * recording's spoken face picks, with its first spoken line standing in where
 * nothing was picked. [picked] says the lines are the face — what someone
 * chose, which is drawn with its heart — rather than words standing in.
 */
export function recordingWords(
  path: string,
  raw: string,
  anchor: string | undefined,
): { lines: string[]; picked: boolean } {
  if (anchor) {
    const topic = transcriptTopics(parseEntity(path, raw)).find((t) => t.anchor === anchor);
    if (topic) return { lines: topic.paragraphs, picked: false };
  }
  const thought = readThought(path, raw);
  if (thought.face.length > 0) return { lines: thought.face, picked: true };
  return { lines: thought.standIn ? [thought.standIn] : [], picked: false };
}

/** An arc's or a nook's face: the spoken picks its `# Face` lists. */
export function faceOf(path: string, raw: string): string[] {
  return picksIn(sectionNamed(parseEntity(path, raw), 'face')?.body ?? '')
    .map((p) => p.text)
    .filter((t) => !t.trimStart().startsWith('{"'));
}

/** The recording's transcript, topic by topic. */
export function transcriptOf(path: string, raw: string): Topic[] {
  return transcriptTopics(parseEntity(path, raw));
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** What an opened recording is titled: the name someone gave it, or when it
 *  was said — `23 September, 20:23`. */
export function recordingTitle(path: string, raw: string): string {
  const thought = readThought(path, raw);
  if (thought.title) return thought.title;
  const m = path.match(/(\d{4})-(\d{2})-(\d{2}) at (\d{2})-(\d{2})/);
  if (!m) return bare(path);
  return `${Number(m[3])} ${MONTH_NAMES[Number(m[2]) - 1]}, ${m[4]}:${m[5]}`;
}

/** [order] with [moving] taken out and put back before [before], or at the
 *  end when [before] is null. Dropping a row before itself changes nothing. */
export function movedBefore(order: readonly string[], moving: string, before: string | null): string[] {
  if (before === moving) return [...order];
  const rest = order.filter((p) => p !== moving);
  const at = before === null ? -1 : rest.indexOf(before);
  rest.splice(at < 0 ? rest.length : at, 0, moving);
  return rest;
}

/** The arcs panel's rows: done ones only when asked for, or when marked done
 *  while the panel is open, so a row does not vanish under the pick. */
export function shownArcRows(rows: readonly Row[], showDone: boolean, keep: ReadonlySet<string>): Row[] {
  return rows.filter((row) => showDone || row.status !== 'done' || keep.has(row.ref.ref));
}

/**
 * A new order for the rows a panel shows, written back into the whole list:
 * each hidden entry keeps its slot and the shown ones fill theirs in the new
 * order. Hiding done arcs must not move them.
 */
export function withShownOrder(all: readonly string[], shownOrder: readonly string[]): string[] {
  const shown = new Set(shownOrder);
  const slots = all.filter((p) => shown.has(p)).length;
  if (slots !== shownOrder.length) return [...shownOrder, ...all.filter((p) => !shown.has(p))];
  let next = 0;
  return all.map((p) => (shown.has(p) ? shownOrder[next++]! : p));
}
